// src/core/utils/codeGenerator.ts

import { getLibraryPseudocode } from './pseudocodeLibrary';

export const CODE_LANGUAGES = ['C', 'C++', 'C#', 'Go', 'Java', 'JavaScript', 'Python', 'Rust'];

// Glob import all code files as raw strings
const codeModules = import.meta.glob('../../algorithms/**/code/*.*', { query: '?raw', eager: true }) as Record<string, string>;

// Glob import all pseudocode files if any exist
const pseudocodeModules = import.meta.glob('../../algorithms/**/pseudocode.*', { query: '?raw', eager: true }) as Record<string, string>;

export function getAlgorithmPseudocode(algorithmName: string): string {
  const name = algorithmName.toLowerCase();
  const library = getLibraryPseudocode(name);
  if (library) return library;

  if (name.includes('bubble')) {
    return `procedure bubbleSort(A : list of sortable items)\n    n := length(A)\n    repeat\n        swapped := false\n        for i := 1 to n-1 inclusive do\n            if A[i-1] > A[i] then\n                swap(A[i-1], A[i])\n                swapped := true\n    until not swapped\nend procedure`;
  }

  if (name.includes('quick')) {
    return `procedure quickSort(A, low, high)\n    if low < high then\n        p := partition(A, low, high)\n        quickSort(A, low, p - 1)\n        quickSort(A, p + 1, high)\n\nprocedure partition(A, low, high)\n    pivot := A[high]\n    i := low\n    for j := low to high - 1 do\n        if A[j] < pivot then\n            swap A[i] with A[j]\n            i := i + 1\n    swap A[i] with A[high]\n    return i`;
  }

  if (name.includes('merge')) {
    return `procedure mergeSort(A, l, r)\n    if l < r then\n        m := floor((l + r) / 2)\n        mergeSort(A, l, m)\n        mergeSort(A, m + 1, r)\n        merge(A, l, m, r)\n\nprocedure merge(A, l, m, r)\n    n1 := m - l + 1\n    n2 := r - m\n    create arrays L[0..n1] and R[0..n2]\n    merge L and R back into A[l..r]`;
  }

  if (name.includes('heap')) {
    return `procedure heapSort(A)\n    n := length(A)\n    for i := n/2 - 1 downto 0 do\n        heapify(A, n, i)\n    for i := n - 1 downto 0 do\n        swap A[0] with A[i]\n        heapify(A, i, 0)\n\nprocedure heapify(A, n, i)\n    largest := i\n    left := 2*i + 1\n    right := 2*i + 2\n    if left < n and A[left] > A[largest] then\n        largest := left\n    if right < n and A[right] > A[largest] then\n        largest := right\n    if largest != i then\n        swap A[i] with A[largest]\n        heapify(A, n, largest)`;
  }

  if (name.includes('insertion')) {
    return `procedure insertionSort(A)\n    i := 1\n    while i < length(A) do\n        x := A[i]\n        j := i - 1\n        while j >= 0 and A[j] > x do\n            A[j + 1] := A[j]\n            j := j - 1\n        A[j + 1] := x\n        i := i + 1`;
  }

  if (name.includes('selection')) {
    return `procedure selectionSort(A)\n    n := length(A)\n    for i := 0 to n - 1 do\n        min_idx := i\n        for j := i + 1 to n do\n            if A[j] < A[min_idx] then\n                min_idx := j\n        swap A[min_idx] with A[i]`;
  }

  if (name.includes('dijkstra')) {
    return `procedure Dijkstra(Graph, source):\n    for each vertex v in Graph:\n        dist[v] := INFINITY\n        prev[v] := UNDEFINED\n        add v to Q\n    dist[source] := 0\n    \n    while Q is not empty:\n        u := vertex in Q with min dist[u]\n        remove u from Q\n        \n        for each neighbor v of u:\n            alt := dist[u] + length(u, v)\n            if alt < dist[v]:\n                dist[v] := alt\n                prev[v] := u`;
  }

  if (name.includes('bfs') || name.includes('breadth')) {
    return `procedure BFS(Graph, start):\n    queue := [start]\n    visited := {start}\n    \n    while queue is not empty do\n        vertex := queue.dequeue()\n        process(vertex)\n        \n        for each neighbor of vertex do\n            if neighbor not in visited then\n                visited.add(neighbor)\n                queue.enqueue(neighbor)`;
  }

  if (name.includes('dfs') || name.includes('depth')) {
    return `procedure DFS(Graph, vertex):\n    visited.add(vertex)\n    process(vertex)\n    \n    for each neighbor of vertex do\n        if neighbor not in visited then\n            DFS(Graph, neighbor)`;
  }

  if (name.includes('binary') && name.includes('search')) {
    return `procedure binarySearch(A, target)\n    low := 0\n    high := length(A) - 1\n    \n    while low <= high do\n        mid := floor((low + high) / 2)\n        if A[mid] == target then\n            return mid\n        else if A[mid] < target then\n            low := mid + 1\n        else\n            high := mid - 1\n    return NOT_FOUND`;
  }

  if (name.includes('fibonacci')) {
    return `procedure fibonacci(n)\n    if n <= 1 then\n        return n\n    return fibonacci(n - 1) + fibonacci(n - 2)\n\nprocedure fibonacciDP(n)\n    if n <= 1 then\n        return n\n    dp := array of size n + 1\n    dp[0] := 0\n    dp[1] := 1\n    for i := 2 to n do\n        dp[i] := dp[i - 1] + dp[i - 2]\n    return dp[n]`;
  }

  if (name.includes('knapsack')) {
    return `procedure knapsack(weights, values, capacity)\n    n := length(weights)\n    dp := 2D array of size (n + 1) x (capacity + 1)\n    \n    for i := 0 to n do\n        for w := 0 to capacity do\n            if i == 0 or w == 0 then\n                dp[i][w] := 0\n            else if weights[i-1] <= w then\n                dp[i][w] := max(values[i-1] + dp[i-1][w-weights[i-1]], dp[i-1][w])\n            else\n                dp[i][w] := dp[i-1][w]\n    return dp[n][capacity]`;
  }

  if (name.includes('lcs') || name.includes('longest') && name.includes('common')) {
    return `procedure LCS(X, Y)\n    m := length(X)\n    n := length(Y)\n    dp := 2D array of size (m + 1) x (n + 1)\n    \n    for i := 0 to m do\n        for j := 0 to n do\n            if i == 0 or j == 0 then\n                dp[i][j] := 0\n            else if X[i-1] == Y[j-1] then\n                dp[i][j] := dp[i-1][j-1] + 1\n            else\n                dp[i][j] := max(dp[i-1][j], dp[i][j-1])\n    return dp[m][n]`;
  }

  if (name.includes('edit') && name.includes('distance')) {
    return `procedure editDistance(word1, word2)\n    m := length(word1)\n    n := length(word2)\n    dp := 2D array of size (m + 1) x (n + 1)\n    \n    for i := 0 to m do\n        dp[i][0] := i\n    for j := 0 to n do\n        dp[0][j] := j\n    \n    for i := 1 to m do\n        for j := 1 to n do\n            if word1[i-1] == word2[j-1] then\n                dp[i][j] := dp[i-1][j-1]\n            else\n                dp[i][j] := 1 + min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1])\n    return dp[m][n]`;
  }

  if (name.includes('avl') || name.includes('tree')) {
    return `procedure insert(root, key)\n    if root is NULL then\n        return newNode(key)\n    if key < root.key then\n        root.left := insert(root.left, key)\n    else if key > root.key then\n        root.right := insert(root.right, key)\n    else\n        return root\n    \n    updateHeight(root)\n    balance := getBalance(root)\n    \n    if balance > 1 and key < root.left.key then\n        return rightRotate(root)\n    if balance < -1 and key > root.right.key then\n        return leftRotate(root)\n    \n    return root`;
  }

  if (name.includes('hash') || name.includes('map')) {
    return `procedure hashInsert(table, key, value)\n    index := hash(key) mod table.size\n    if table[index] is empty then\n        table[index] := (key, value)\n    else\n        handleCollision(table, index, key, value)\n\nprocedure hashSearch(table, key)\n    index := hash(key) mod table.size\n    if table[index].key == key then\n        return table[index].value\n    else\n        return searchCollisionChain(table, index, key)`;
  }

  // Generic fallback with a real, structured algorithmic outline
  return `procedure ${algorithmName.replace(/\s+/g, '')}(data)\n\n    state := initialize(data)\n    \n\n    while not isComplete(state) do\n    \n        state := apply(state, candidate)\n        if invalid(state) then\n            state := rollback(state)\n    \n    return extractResult(state)\nend procedure`;
}

export function generateDynamicBoilerplate(algorithmName: string, language: string): string {
  const normLang = language.toLowerCase();
  const algoClass = algorithmName.replace(/\s+/g, '');

  switch (normLang) {
    case 'python':
      return `def ${algoClass.toLowerCase()}(data):\n    # Подготовка данных для визуализации\n    print(f"Running ${algorithmName}...")\n    # Точка входа для вашего алгоритма\n    pass`;
    case 'javascript':
      return `function ${algoClass.toLowerCase()}(data) {\n    console.log("Running ${algorithmName}...");\n    // Ваш код здесь\n    return data;\n}`;
    case 'c++':
      return `#include <iostream>\n#include <vector>\n\nvoid run${algoClass}(std::vector<int>& data) {\n    std::cout << "Running ${algorithmName}" << std::endl;\n    // Реализация\n}`;
    case 'java':
      return `public class ${algoClass} {\n    public static void main(String[] args) {\n        System.out.println("Running ${algorithmName}");\n    }\n}`;
    default:
      return `// Напишите вашу реализацию ${algorithmName} на языке ${language}`;
  }
}

/**
 * Loads the actual, complete algorithm code file if it exists in the codebase assets,
 * falling back to generateDynamicBoilerplate if not found.
 */
export function getAlgorithmCode(algorithmId: string, language: string): string {
  const extMap: Record<string, string> = {
    'c': '.c',
    'c++': '.cpp',
    'c#': '.cs',
    'go': '.go',
    'java': '.java',
    'javascript': '.js',
    'python': '.py',
    'rust': '.rs'
  };

  const ext = extMap[language.toLowerCase()];
  if (!ext) return `// Language ${language} not supported`;

  const normalizedId = algorithmId.toLowerCase();
  const underscoreId = normalizedId.replace(/-/g, '_');
  const hyphenId = normalizedId.replace(/_/g, '-');

  const targetKey = Object.keys(codeModules).find(key => {
    const keyLower = key.toLowerCase();
    const containsDir = keyLower.includes(`/${normalizedId}/`) ||
                        keyLower.includes(`/${underscoreId}/`) ||
                        keyLower.includes(`/${hyphenId}/`);
    
    const containsFile = keyLower.endsWith(`/${normalizedId}${ext}`) ||
                         keyLower.endsWith(`/${underscoreId}${ext}`) ||
                         keyLower.endsWith(`/${hyphenId}${ext}`);

    return containsDir && containsFile;
  });

  if (targetKey && codeModules[targetKey]) {
    const mod = codeModules[targetKey] as Record<string, unknown> | string;
    return typeof mod === 'string' ? mod : (String(mod.default ?? mod) || '');
  }

  return `// No real ${language} implementation file was found for ${algorithmId}.
// Add it under frontend/src/algorithms/<category>/${algorithmId}/code/${algorithmId}${ext}.`;
}

/**
 * Loads the actual pseudocode file if it exists, falling back to the standard
 * getAlgorithmPseudocode generator.
 */
export function getAlgorithmPseudocodeFile(algorithmId: string, algorithmName: string): string {
  const normalizedId = algorithmId.toLowerCase();
  const underscoreId = normalizedId.replace(/-/g, '_');
  const hyphenId = normalizedId.replace(/_/g, '-');

  const targetKey = Object.keys(pseudocodeModules).find(key => {
    const keyLower = key.toLowerCase();
    return (keyLower.includes(`/${normalizedId}/`) ||
            keyLower.includes(`/${underscoreId}/`) ||
            keyLower.includes(`/${hyphenId}/`)) &&
           keyLower.includes('/pseudocode.');
  });

  if (targetKey && pseudocodeModules[targetKey]) {
    const mod = pseudocodeModules[targetKey] as Record<string, unknown> | string;
    return typeof mod === 'string' ? mod : (String(mod.default ?? mod) || '');
  }

  return getAlgorithmPseudocode(algorithmName);
}
